import { Link } from "react-router-dom";
import {
  Gamepad2,
  Car,
  Sparkles,
  Heart,
  Hand,
  Smile,
  Cloud,
  Sun,
  Star,
  Coffee,
  ArrowUpRight,
  Check,
  X,
  Printer,
} from "lucide-react";

const things = [
  {
    icon: Cloud,
    title: "Brain fog",
    body: "Sometimes thoughts feel slow, like wading through a soft cloud.",
    testid: "kids-thing-fog",
  },
  {
    icon: Star,
    title: "Wobbles & shakes",
    body: "Arms or legs might tremble or move on their own — like a tickle they can't stop.",
    testid: "kids-thing-shakes",
  },
  {
    icon: Sun,
    title: "Sudden tiredness",
    body: "Even small things can use up a whole battery's worth of energy.",
    testid: "kids-thing-tired",
  },
  {
    icon: Coffee,
    title: "Bumpy days, smooth days",
    body: "Some days are tricky and some days are easy. Both are okay.",
    testid: "kids-thing-days",
  },
];

const helps = [
  {
    icon: Heart,
    title: "Be a calm friend",
    body: "Speak softly. A kind voice is like a hand to hold.",
    testid: "kids-help-calm",
  },
  {
    icon: Hand,
    title: "Don't rush them",
    body: "Wait while their remote finds the right channel. There's no hurry.",
    testid: "kids-help-wait",
  },
  {
    icon: Smile,
    title: "Treat them normally",
    body: "They're still the same friend. Still funny. Still them.",
    testid: "kids-help-normal",
  },
  {
    icon: Sparkles,
    title: "Believe them",
    body: "If they say something feels weird in their body — it really does.",
    testid: "kids-help-believe",
  },
];

const truths = [
  { text: "FND is real.", testid: "truth-real" },
  { text: "It is not their fault.", testid: "truth-not-fault" },
  { text: "It is not catching — you can't get it from a hug.", testid: "truth-not-catching" },
  { text: "Their brain is clever, just a little crossed-up.", testid: "truth-clever" },
];

const myths = [
  { text: "They are not making it up.", testid: "myth-making-up" },
  { text: "They are not being lazy.", testid: "myth-lazy" },
  { text: "They can't just \"try harder\" to stop it.", testid: "myth-try-harder" },
  { text: "It doesn't mean they're broken.", testid: "myth-broken" },
];

export default function ForKids() {
  return (
    <div data-testid="page-for-kids">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Soft sun-like decorative blobs */}
        <div className="pointer-events-none absolute top-20 -right-32 w-[28rem] h-[28rem] rounded-full bg-terracotta/15 blur-3xl" />
        <div className="pointer-events-none absolute -top-10 -left-32 w-80 h-80 rounded-full bg-moss/15 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-8">
            <span className="w-6 h-px bg-moss" />
            For kids, parents & carers
          </span>
          <h1
            data-testid="kids-title"
            className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight max-w-5xl text-balance"
          >
            A short story about a
            <span className="italic text-terracotta"> tricky remote </span>
            and a
            <span className="italic text-terracotta"> little car</span>.
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-charcoal-light dark:text-bone/70 leading-relaxed max-w-2xl">
            A simple way to explain FND to a child — whether they have it
            themselves or know someone who does. Read it together. Take
            your time.
          </p>

          <div className="mt-8">
            <Link
              to="/for-kids/print"
              data-testid="kids-print-link"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-charcoal/20 dark:border-bone/30 text-charcoal dark:text-bone text-sm font-medium tracking-wide hover:border-terracotta hover:text-terracotta transition-all duration-500"
            >
              <Printer size={16} strokeWidth={1.5} />
              Get the printable booklet
            </Link>
          </div>
        </div>
      </section>

      {/* THE ANALOGY — centerpiece */}
      <section className="pb-20 lg:pb-28" data-testid="kids-analogy-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-bone-100 dark:bg-charcoal-light/15 border border-bone-200 dark:border-charcoal-light/25 p-8 sm:p-12 lg:p-16">
            {/* decorative dots */}
            <div className="pointer-events-none absolute top-8 right-8 flex gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta/60" />
              <span className="w-1.5 h-1.5 rounded-full bg-moss/60" />
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta/40" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Visual: remote + zigzag + car */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="relative flex items-center justify-center gap-3 sm:gap-6 py-6">
                  {/* Remote */}
                  <div className="relative">
                    <div
                      data-testid="kids-remote"
                      className="w-24 h-32 sm:w-28 sm:h-40 rounded-3xl bg-terracotta text-bone flex flex-col items-center justify-center gap-2 shadow-[0_15px_40px_-15px_rgba(200,121,102,0.6)]"
                    >
                      <Gamepad2 size={32} strokeWidth={1.5} />
                      <span className="text-[0.6rem] uppercase tracking-[0.2em] font-medium">
                        Brain
                      </span>
                    </div>
                    <span className="absolute -top-3 -right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-bone dark:bg-charcoal-light border border-bone-200 dark:border-charcoal-light/40 text-terracotta">
                      <Sparkles size={14} strokeWidth={1.5} />
                    </span>
                  </div>

                  {/* Zigzag signal */}
                  <svg
                    width="80"
                    height="36"
                    viewBox="0 0 80 36"
                    className="text-charcoal-light dark:text-bone/60 shrink-0"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 18 L12 6 L22 30 L32 6 L42 30 L52 6 L62 30 L72 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      strokeDasharray="3 4"
                    />
                  </svg>

                  {/* Car */}
                  <div className="relative">
                    <div
                      data-testid="kids-car"
                      className="w-28 h-24 sm:w-36 sm:h-28 rounded-3xl bg-moss text-bone flex flex-col items-center justify-center gap-1 shadow-[0_15px_40px_-15px_rgba(139,160,130,0.55)]"
                    >
                      <Car size={32} strokeWidth={1.5} />
                      <span className="text-[0.6rem] uppercase tracking-[0.2em] font-medium">
                        Body
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-5">
                  <span className="w-6 h-px bg-terracotta" />
                  The tiny story
                </span>
                <blockquote
                  data-testid="kids-quote"
                  className="font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal dark:text-bone leading-snug text-balance"
                >
                  “Your brain is like the remote, and your body is like the
                  car. Sometimes the signal gets <em className="text-terracotta">fuzzy</em>,
                  so the car might turn or stop even when you didn’t press the button.“
                </blockquote>
                <p className="mt-6 text-base text-charcoal-light dark:text-bone/65 leading-relaxed max-w-xl">
                  And that’s all FND really is — a fuzzy signal between a
                  brain that’s working hard and a body that’s listening.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THINGS THAT MIGHT HAPPEN */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="kids-things-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
              <span className="w-6 h-px bg-moss" />
              What it might look like
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              Some things that <em className="text-terracotta">might happen</em>.
            </h2>
            <p className="mt-5 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
              None of these mean anything is wrong with who they are. They
              just mean the signal got a bit fuzzy for a while.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {things.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.testid}
                  data-testid={t.testid}
                  className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-7 hover-lift"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta mb-5">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-xl text-charcoal dark:text-bone tracking-tight mb-2">
                    {t.title}
                  </h3>
                  <p className="text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                    {t.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW YOU CAN HELP */}
      <section className="py-20 lg:py-28" data-testid="kids-helps-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              Being a good friend
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              Four small ways to <em className="text-terracotta">really help</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {helps.map((h, i) => {
              const Icon = h.icon;
              return (
                <div
                  key={h.testid}
                  data-testid={h.testid}
                  className="group relative rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-7 hover-lift"
                >
                  <span className="absolute top-6 right-6 text-xs font-mono text-charcoal-light/60 dark:text-bone/40">
                    0{i + 1}
                  </span>
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-moss/15 text-moss mb-5 transition-all duration-500 group-hover:bg-moss group-hover:text-bone">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-xl text-charcoal dark:text-bone tracking-tight mb-2">
                    {h.title}
                  </h3>
                  <p className="text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                    {h.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUE / NOT TRUE */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="kids-truths-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
              <span className="w-6 h-px bg-moss" />
              Just so we’re clear
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              The <em className="text-terracotta">true</em> things, and the things that aren’t.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              data-testid="truths-card"
              className="rounded-3xl border border-moss/30 bg-moss/10 dark:bg-moss/15 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-moss text-bone">
                  <Check size={18} strokeWidth={2} />
                </span>
                <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
                  This is true
                </h3>
              </div>
              <ul className="space-y-3">
                {truths.map((t) => (
                  <li
                    key={t.testid}
                    data-testid={t.testid}
                    className="flex items-start gap-3 text-base text-charcoal dark:text-bone"
                  >
                    <Check size={16} strokeWidth={2} className="text-moss mt-1 shrink-0" />
                    <span>{t.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="myths-card"
              className="rounded-3xl border border-terracotta/30 bg-terracotta/10 dark:bg-terracotta/15 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-terracotta text-bone">
                  <X size={18} strokeWidth={2} />
                </span>
                <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
                  These aren’t true
                </h3>
              </div>
              <ul className="space-y-3">
                {myths.map((m) => (
                  <li
                    key={m.testid}
                    data-testid={m.testid}
                    className="flex items-start gap-3 text-base text-charcoal dark:text-bone"
                  >
                    <X size={16} strokeWidth={2} className="text-terracotta mt-1 shrink-0" />
                    <span>{m.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PARENT NOTE */}
      <section className="py-20 lg:py-28" data-testid="kids-parent-note">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="rounded-[2rem] border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-10 lg:p-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              For the grown-up reading along
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl text-charcoal dark:text-bone leading-snug tracking-tight text-balance">
              You don’t need to have all the answers. The remote-and-car story is enough.
            </h3>
            <p className="mt-5 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
              If a child asks more, follow their curiosity. If they go quiet,
              that’s okay too. Most of all — let them know that the person
              with FND is still the same person, on smooth days and bumpy ones.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/resources"
            data-testid="kids-cta-resources"
            className="group relative block overflow-hidden rounded-[2rem] bg-moss text-bone p-10 lg:p-14 hover-lift"
          >
            <span className="text-xs uppercase tracking-[0.28em] text-bone/70">
              When you want more
            </span>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Helplines, books & quiet resources.
            </h3>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/50 pb-1 group-hover:border-bone transition">
              Open resources
              <ArrowUpRight
                size={16}
                strokeWidth={1.5}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
            <div className="pointer-events-none absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-bone/20 blur-3xl" />
          </Link>
        </div>
      </section>
    </div>
  );
}
